// A timeline of every compensated-hours credit or claim a person has ever had,
// grouped by month, newest first — and the one way to undo a wrong one: revoke
// it with a reason, rather than pretend it never happened
// (20260922110000_comp_hours_revoke.sql).
//
// Read-only, with two exceptions on an approved row: Revoke, and Edit, which
// revokes it and credits the correction in one atomic call (bug 71ad284c,
// 20261012100000). Both are gated on canRevoke — the same audience that may
// grant (grantRefusal in PeopleHRScreen), computed by the caller so this
// component only renders what it is told. A revoked row keeps its place in the
// timeline, naming who revoked it and why; it does not disappear the way a
// delete would. No attention count (nobody acts on a past month from here) and
// no CSV export — this is a trail, not a report.
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Mono } from './primitives';
import { PCard } from './people';
import { Eyebrow } from './card';
import { Field, TextInput } from './field';
import { useToast } from './feedback';
import { RequestStatus } from './requests';
import { CompHoursModal } from './compHours';
import { fmtDayKey } from '../lib/dates';
import { monthLabel } from '../lib/payrollPeriods';
import {
  listForPerson,
  revokeCredit,
  fmtHours,
  type CompHoursTrailRow,
  type Category,
} from '../lib/compHours';

const monthKey = (iso: string) => iso.slice(0, 7); // YYYY-MM

const labelOf = (categories: readonly Category[], id: string) =>
  categories.find((c) => c.id === id)?.label ?? id;

/** Rows arrive newest-first from listForPerson; grouping preserves that order
 *  both across months and within one. */
function byMonth(rows: readonly CompHoursTrailRow[]) {
  const out: { key: string; label: string; rows: CompHoursTrailRow[] }[] = [];
  for (const r of rows) {
    const key = monthKey(r.date);
    const bucket = out.at(-1);
    if (bucket?.key === key) bucket.rows.push(r);
    else
      out.push({
        key,
        label: monthLabel(Number(key.slice(0, 4)), Number(key.slice(5, 7))),
        rows: [r],
      });
  }
  return out;
}

export function CompHoursTrail({
  profileId,
  canRevoke,
  categories,
  today,
  personName,
  reloadKey,
  onChanged,
  action,
}: {
  profileId: string;
  canRevoke: boolean;
  categories: readonly Category[];
  /** Org-local today, for Edit — the database refuses a later day. */
  today: string;
  /** Labels the Edit dialog only. */
  personName?: string | null;
  reloadKey: number;
  onChanged: () => void;
  /**
   * The card's own action, in its header: on the Hours tab, Credit hours. An
   * action lives on the card it changes; the button used to float between two
   * cards (HRP-16). With one, the card renders even with no history, so the
   * action has somewhere to be.
   */
  action?: ReactNode;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<CompHoursTrailRow[]>([]);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<CompHoursTrailRow | null>(null);

  useEffect(() => {
    void listForPerson(profileId).then(setRows);
  }, [profileId, reloadKey]);

  const startRevoke = (r: CompHoursTrailRow) => {
    setRevokingId(r.id);
    setReason('');
    setErr(null);
  };
  const cancelRevoke = () => {
    setRevokingId(null);
    setErr(null);
  };
  const confirmRevoke = async (r: CompHoursTrailRow) => {
    if (!reason.trim()) return;
    setBusy(true);
    setErr(null);
    const { error } = await revokeCredit(r.id, reason.trim());
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setRevokingId(null);
    toast.push({
      tone: 'success',
      message: `Revoked ${fmtHours(r.hours)} credited for ${fmtDayKey(r.date)}.`,
    });
    onChanged();
  };

  if (!rows.length && !action) return null;

  return (
    <PCard label="Credit history" right={action}>
      {!rows.length && (
        <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          No credits or claims yet.
        </div>
      )}
      {byMonth(rows).map((month) => (
        <div key={month.key}>
          <Eyebrow as="h3" style={{ padding: 'var(--hf-space-3) 0 var(--hf-space-1)' }}>
            {month.label}
          </Eyebrow>
          {month.rows.map((r) => (
            <div
              key={r.id}
              style={{ padding: 'var(--hf-space-2) 0', borderTop: '1px solid var(--hf-rule)' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2) var(--hf-space-3)',
                  fontSize: 'var(--hf-text-sm)',
                }}
              >
                {/* The check-in history's row, so the two read alike (bug report
                    2026-09-22: "approved should be on the left and the
                    credited hours … on the right, follow the standard
                    structure"): the day and its state on the left, what
                    happened in the middle, the hours right-aligned like a
                    day's total. */}
                <span style={{ minWidth: '6rem', flexShrink: 0, color: 'var(--hf-ink)' }}>
                  {fmtDayKey(r.date, { weekday: true })}
                </span>
                <RequestStatus status={r.status} />
                {/* Wraps: the note was cut off and readable only in a hover title. */}
                <span
                  style={{
                    flex: '1 1 10rem',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    color: 'var(--hf-ink-3)',
                  }}
                >
                  {labelOf(categories, r.category)}
                  {r.note && ` — ${r.note}`}
                  {/* Not on a revoked row: beside "Revoked" it read as the revoker,
                      who is named on the line below. */}
                  {r.approver_name && r.status !== 'revoked' && (
                    <span style={{ fontSize: 'var(--hf-text-xs)' }}> · by {r.approver_name}</span>
                  )}
                </span>
                {canRevoke && r.status === 'approved' && revokingId !== r.id && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--hf-space-1)' }}>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startRevoke(r)}>
                      Revoke
                    </Button>
                  </span>
                )}
                <Mono
                  style={{
                    marginLeft:
                      canRevoke && r.status === 'approved' && revokingId !== r.id
                        ? undefined
                        : 'auto',
                    minWidth: '4rem',
                    textAlign: 'right',
                    fontSize: 'var(--hf-text-md)',
                    color: r.status === 'approved' ? 'var(--hf-ink)' : 'var(--hf-ink-3)',
                    textDecoration: r.status === 'revoked' ? 'line-through' : undefined,
                  }}
                >
                  +{fmtHours(r.hours)}
                </Mono>
              </div>

              {r.status === 'revoked' && r.revoke_reason && (
                <div
                  style={{
                    marginTop: 'var(--hf-space-1)',
                    fontSize: 'var(--hf-text-xs)',
                    color: 'var(--hf-ink-3)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  Revoked{r.revoker_name && ` by ${r.revoker_name}`} — {r.revoke_reason}
                </div>
              )}

              {revokingId === r.id && (
                <div
                  style={{
                    marginTop: 'var(--hf-space-2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--hf-space-2)',
                  }}
                >
                  <Field
                    label="Reason for revoking"
                    required
                    error={err ?? undefined}
                    hint="They will see this on their own record."
                  >
                    <TextInput dense autoFocus value={reason} onChange={setReason} />
                  </Field>
                  <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
                    <Button variant="ghost" size="sm" onClick={cancelRevoke}>
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      loading={busy}
                      disabled={!reason.trim()}
                      onClick={() => void confirmRevoke(r)}
                    >
                      Confirm revoke
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
      {editing && (
        <CompHoursModal
          mode="grant"
          uid={profileId}
          personName={personName}
          categories={categories}
          today={today}
          correcting={editing}
          onClose={() => setEditing(null)}
          onFiled={onChanged}
        />
      )}
    </PCard>
  );
}
